import { useEffect, useState, useRef } from "react";
import { useAuthContext } from "../context/auth";
import useAudio from "./useAudio";

const useCountListDown = (
  initialValues: number[],
  callback: () => void,
  playSound?: boolean,
  sendToServer?: boolean,
  index?: number
) => {
  const { did, sendCurrentIndex } = useAuthContext();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [count, setCount] = useState(initialValues[0]);
  const [isRunning, setIsRunning] = useState(false);
  const play = useAudio("countdown.mp3");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const syncStartTimeRef = useRef<number | null>(null);
  const accumulatedTimeRef = useRef<number>(0);

  // useEffect(() => {
  //   if (sendToServer) {
  //     console.log("send init index");
  //     sendCurrentIndex(0);
  //   } else {
  //     sendCurrentIndex(undefined);
  //   }
  // }, [sendToServer]);

  useEffect(() => {
    if (index !== undefined) {
      console.log("received index", index);
      setCurrentIndex(index);
      setCount(initialValues[index]);
    }
  }, [index]);

  const handleCurrentIndex = (index: number) => {
    setCurrentIndex(index);
    if (sendToServer) {
      console.log("send current index");
      sendCurrentIndex(index);
    }
  };

  const start = (syncStartTime?: number) => {
    syncStartTimeRef.current = syncStartTime || Date.now();
    // Calculate accumulated time for all previous intervals
    accumulatedTimeRef.current = initialValues
      .slice(0, currentIndex)
      .reduce((sum, val) => sum + val * 1000, 0);
    setIsRunning(true);
  };

  const stop = () => {
    setIsRunning(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const reset = () => {
    handleCurrentIndex(0);
    setCount(initialValues[0]);
  };

  useEffect(() => {
    if (isRunning && syncStartTimeRef.current) {
      const startTime = syncStartTimeRef.current;

      const tick = () => {
        const currentTime = Date.now();
        const totalElapsed = currentTime - startTime;

        // If startTime is in the future, show initial values
        if (totalElapsed < 0) {
          setCount(initialValues[0]);
          timerRef.current = setTimeout(tick, Math.abs(totalElapsed));
          return;
        }

        // Calculate which index we should be at based on absolute time
        let accumulatedTime = 0;
        let targetIndex = 0;

        for (let i = 0; i < initialValues.length; i++) {
          const duration = initialValues[i] * 1000;
          if (totalElapsed < accumulatedTime + duration) {
            targetIndex = i;
            break;
          }
          accumulatedTime += duration;
          targetIndex = i + 1;
        }

        // If we've gone past all intervals, finish
        if (targetIndex >= initialValues.length) {
          callback();
          reset();
          stop();
          return;
        }

        // Update index if it changed
        if (targetIndex !== currentIndex) {
          // Skip zero-duration items
          while (targetIndex < initialValues.length && initialValues[targetIndex] === 0) {
            targetIndex++;
          }

          if (targetIndex >= initialValues.length) {
            callback();
            reset();
            stop();
            return;
          }

          handleCurrentIndex(targetIndex);

          // Recalculate accumulated time for new index
          accumulatedTime = initialValues
            .slice(0, targetIndex)
            .reduce((sum, val) => sum + val * 1000, 0);
        }

        // Calculate count for current index
        const elapsedInCurrentInterval = totalElapsed - accumulatedTime;
        const secondsPassedInInterval = Math.floor(elapsedInCurrentInterval / 1000);
        const newCount = Math.max(
          0,
          initialValues[targetIndex] - secondsPassedInInterval
        );

        setCount((prevCount) => {
          if (newCount !== prevCount) {
            console.log(newCount);

            if (newCount === 4) {
              if (playSound) {
                did === "L1" && play();
              }
            }
          }

          return newCount;
        });

        // Calculate next tick to align with second boundaries
        const nextSecondBoundary = Math.ceil(elapsedInCurrentInterval / 1000) * 1000;
        const nextTickTime = startTime + accumulatedTime + nextSecondBoundary;
        const delay = Math.max(0, nextTickTime - Date.now());

        if (newCount > 0 || targetIndex < initialValues.length - 1) {
          timerRef.current = setTimeout(tick, delay);
        }
      };

      // Start immediately to show initial count
      tick();

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }
  }, [isRunning, currentIndex, initialValues, play, callback, playSound, did]);

  return {
    count,
    currentIndex,
    start,
    stop,
    reset,
    stopped: !isRunning,
    totalSteps: initialValues.length,
  };
};

export default useCountListDown;
